import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TablePagination,
  Link,
  Button,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import { useNavigate, useLocation } from "react-router-dom";
import { Block } from "../GhcAi/Utils/ComponentsUtils";

interface QuantAnalysisData {
  count: number;
  deal_type: string;
  data: QuantTicker[];
}

interface QuantTicker {
  ticker: string;
  run_date: string;
  pricing_date: string | null;
  region: string;
  deal_type: string;
  unique_deal_id: string;
  issuer_name: string;
  sector: string;
  quant_analysis?: Block[] | string;
  quant_signal?: string;
  created_at: string;
  updated_at: string;
}

const ShowQuantAnalysis: React.FC = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const rawDealType =
    searchParams.get("deal_type") || searchParams.get("dealType") || "IPO";
  const initialDealType = rawDealType.toUpperCase() === "FO" ? "FO" : "IPO";
  const initialPage = Number(searchParams.get("page") || 0);
  const initialRowsPerPage = Number(searchParams.get("rowsPerPage") || 10);

  const [tickers, setTickers] = useState<QuantTicker[]>([]);
  const [filteredTickers, setFilteredTickers] = useState<QuantTicker[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(Number.isNaN(initialPage) ? 0 : initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(
    Number.isNaN(initialRowsPerPage) ? 10 : initialRowsPerPage
  );
  const [dealType, setDealType] = useState<"IPO" | "FO">(initialDealType);
  const [sortColumn, setSortColumn] = useState<string>("run_date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // ── "Re-run in EMMY" dialog state + helpers ──────────────────────────
  // Agent naming follows the same {display}-{name} ({abbr}) format as
  // the FO-Run popup so all flows read the same. "Run a fresh" +
  // "preview before any save/upload" keeps the workflow fresh and
  // read-only by default. Pricing date is omitted for Upcoming deals
  // where the row has no date.
  const buildEmmyQuery = (row: QuantTicker, dt: "IPO" | "FO") => {
    const pricingDate = row.pricing_date
      ? new Date(row.pricing_date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : null;
    return pricingDate
      ? `Run a fresh Quant-MIDAS Quant Agent (MQA) workflow for ${row.ticker} - ${dt} (priced ${pricingDate}). Show the preview before any save/upload.`
      : `Run a fresh Quant-MIDAS Quant Agent (MQA) workflow for ${row.ticker} - ${dt}. Show the preview before any save/upload.`;
  };

  const [rerunRow, setRerunRow] = useState<QuantTicker | null>(null);
  const [rerunMode, setRerunMode] = useState<"new" | "append">("new");
  const [rerunQuery, setRerunQuery] = useState<string>("");

  const openRerunDialog = (row: QuantTicker) => {
    setRerunRow(row);
    setRerunMode("new");
    setRerunQuery(buildEmmyQuery(row, dealType));
  };

  const closeRerunDialog = () => {
    setRerunRow(null);
  };

  const confirmRerun = () => {
    if (!rerunRow) return;
    const q = rerunQuery.trim();
    if (!q) return;
    const params = new URLSearchParams({
      prefill: q,
      mode: rerunMode,
      autosend: "1",
    });
    navigate(`/openclaw_chat?${params.toString()}`);
  };

  useEffect(() => {
    fetchQuantAnalysis();
  }, [dealType]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set("deal_type", dealType);
    params.delete("dealType");
    params.set("page", String(page));
    params.set("rowsPerPage", String(rowsPerPage));
    const nextSearch = `?${params.toString()}`;
    if (nextSearch !== location.search) {
      navigate(`${location.pathname}${nextSearch}`, { replace: true });
    }
  }, [dealType, page, rowsPerPage, location.pathname, location.search, navigate]);

  useEffect(() => {
    const filtered = tickers.filter(
      (ticker) =>
        ticker.ticker.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticker.issuer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticker.sector.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const sorted = sortTickers(filtered, sortColumn, sortDirection);
    setFilteredTickers(sorted);
  }, [searchQuery, tickers, sortColumn, sortDirection]);

  const fetchQuantAnalysis = async () => {
    if (!apiUrl) {
      setError("REACT_APP_API_URL is not set.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("access_token");

      const res = await fetch(
        `${apiUrl}/api/quant_agent/all_tickers_latest/?deal_type=${dealType}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );

      const data: QuantAnalysisData | any = await res.json();

      if (!res.ok) {
        throw new Error(
          data?.error ||
            data?.detail ||
            `Request failed with status ${res.status}`
        );
      }

      setTickers(data.data || []);
      setFilteredTickers(data.data || []);
    } catch (err: any) {
      setError(err?.message || "Unable to load quant analysis.");
      setTickers([]);
      setFilteredTickers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (ticker: QuantTicker) => {
    if (dealType === "IPO") {
      navigate(
        `/quant-analysis/ipo-dashboard/${encodeURIComponent(ticker.ticker)}?deal_type=IPO&page=${page}&rowsPerPage=${rowsPerPage}`
      );
      return;
    }

    if (dealType === "FO") {
      navigate(
        `/quant-analysis/technical-details/${encodeURIComponent(ticker.ticker)}?deal_type=FO&page=${page}&rowsPerPage=${rowsPerPage}`,
        {
          state: {
            deal: ticker,
            quantAnalysis: ticker.quant_analysis,
          },
        }
      );
      return;
    }

    navigate(
      `/quant-analysis/${encodeURIComponent(ticker.ticker)}?deal_type=${dealType}&page=${page}&rowsPerPage=${rowsPerPage}`
    );
  };

  const sortTickers = (
    data: QuantTicker[],
    column: string,
    direction: "asc" | "desc"
  ) => {
    const sorted = [...data].sort((a, b) => {
      let aVal: any = a[column as keyof QuantTicker];
      let bVal: any = b[column as keyof QuantTicker];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return direction === "asc" ? 1 : -1;
      if (bVal == null) return direction === "asc" ? -1 : 1;

      if (column === "pricing_date" || column === "run_date") {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else if (typeof aVal === "string" && typeof bVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (direction === "asc") {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      }

      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    });

    return sorted;
  };

  const parseQuantAnalysis = (value: Block[] | string | undefined): any => {
    if (!value) return null;

    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }

    if (Array.isArray(value)) return value;

    return null;
  };

  const getSummaryFromBlocks = (
    blocks: Block[] | string | undefined
  ): string => {
    const parsed = parseQuantAnalysis(blocks);

    if (!parsed) return "No summary available";

    if (!Array.isArray(parsed)) {
      // Newest shape — headline summary lives at the root.
      if (typeof parsed?.summary === "string" && parsed.summary.trim()) {
        return parsed.summary;
      }

      // Newest shape — fund-manager narrative is a good fallback.
      if (
        typeof parsed?.ai_summary?.fund_manager_summary === "string" &&
        parsed.ai_summary.fund_manager_summary.trim()
      ) {
        return parsed.ai_summary.fund_manager_summary;
      }

      // V2 flat shape — primitive under event_quality_engine.ai_summary.
      if (
        typeof parsed?.dashboard?.event_quality_engine?.ai_summary === "string" &&
        parsed.dashboard.event_quality_engine.ai_summary.trim()
      ) {
        return parsed.dashboard.event_quality_engine.ai_summary;
      }

      // Legacy VM-wrapped shape — institutional_summary.summary.value.
      const vmSummary =
        parsed?.dashboard?.event_quality_engine?.institutional_summary?.summary?.value;
      if (vmSummary) return vmSummary;

      return (
        parsed?.verdict?.summary ||
        parsed?.institutional_view?.pm_decision ||
        parsed?.analysis?.trend?.summary ||
        "No summary available"
      );
    }

    const textBlocks = parsed.filter((b: any) => b.type === "text");

    if (textBlocks.length > 0) {
      const lastTextBlock = textBlocks[textBlocks.length - 1] as any;
      const content = lastTextBlock.content || "";

      if (content) {
        return content.substring(0, 120) + "...";
      }
    }

    const cardBlocks = parsed.filter((b: any) => b.type === "card");

    if (cardBlocks.length > 0) {
      const firstCard = cardBlocks[0] as any;
      const content = firstCard.description || "";

      if (content) {
        return content.substring(0, 120) + "...";
      }
    }

    return "No summary available";
  };

  const getSentimentFromBlocks = (
    blocks: Block[] | string | undefined
  ): string => {
    const parsed = parseQuantAnalysis(blocks);

    if (!parsed) return "—";

    if (!Array.isArray(parsed)) {
      return parsed?.verdict?.label || parsed?.signal || "—";
    }

    const cardBlocks = parsed.filter((b: any) => b.type === "card");
    const executiveCard = cardBlocks.find(
      (b: any) => b.subtitle === "Executive Verdict"
    );

    return executiveCard?.title || "—";
  };

  /**
   * Conservative keyword mapping from `trade_setup.trade_bias` (or any
   * descriptive directional phrase) onto BUY / SELL / HOLD. Used only as a
   * last-resort fallback when no explicit signal field is set on the row.
   */
  const deriveSignalFromBias = (bias: string | undefined): string | undefined => {
    if (!bias) return undefined;
    const s = bias.toLowerCase();
    // Sell keywords first so phrases like "avoid aggressive long exposure" land on SELL.
    if (/\b(sell|avoid|defensive|bearish|short)\b/.test(s)) return "SELL";
    if (/\b(bullish|accumulate|long|buy)\b/.test(s))        return "BUY";
    if (/\b(hold|neutral|watch|monitor|wait)\b/.test(s))    return "HOLD";
    return undefined;
  };

  const getSignalColor = (signal: string | undefined) => {
    if (!signal) return { bg: "#f1f5f9", color: "#64748b" };

    const s = signal.toLowerCase();

    if (/buy|strong buy/.test(s)) {
      return { bg: "#dcfce7", color: "#166534" };
    }

    if (/sell|strong sell|avoid/.test(s)) {
      return { bg: "#fee2e2", color: "#991b1b" };
    }

    if (/hold|neutral/.test(s)) {
      return { bg: "#fef3c7", color: "#92400e" };
    }

    if (/wait|watchlist|watch/.test(s)) {
      return { bg: "#e2e8f0", color: "#475569" };
    }

    return { bg: "#f1f5f9", color: "#64748b" };
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setPage(0);
  };

  const SortableHeader = ({
    column,
    label,
    width,
  }: {
    column: string;
    label: string;
    width?: string | number;
  }) => (
    <TableCell
      sx={{
        fontWeight: 700,
        color: "#0f172a",
        fontSize: "0.85rem",
        cursor: "pointer",
        userSelect: "none",
        whiteSpace: "nowrap",
        width: width ?? "auto",
        "&:hover": {
          backgroundColor: "#f1f5f9",
        },
      }}
      onClick={() => handleSort(column)}
    >
      <TableSortLabel
        active={sortColumn === column}
        direction={sortColumn === column ? sortDirection : "asc"}
        sx={{
          "& .MuiTableSortLabel-icon": {
            color: sortColumn === column ? "#4f46e5" : "#cbd5e1",
          },
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: "auto",
        px: { xs: 1, sm: 2, md: 3 },
        py: 3,
      }}
    >
      <Box sx={{ mb: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <InsightsRoundedIcon sx={{ fontSize: 20, color: "#fff" }} />
            </Box>

            <Typography
              sx={{
                fontSize: "1.35rem",
                fontWeight: 700,
                color: "#0f172a",
                letterSpacing: "-0.02em",
              }}
            >
              Quant Analysis
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={() => {
                setDealType("IPO");
                setPage(0);
              }}
              sx={{
                px: 2.5,
                py: 0.8,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: "0.85rem",
                textTransform: "none",
                border: `2px solid ${
                  dealType === "IPO" ? "#4f46e5" : "#e2e8f0"
                }`,
                color: dealType === "IPO" ? "#4f46e5" : "#94a3b8",
                backgroundColor: dealType === "IPO" ? "#eef2ff" : "transparent",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#4f46e5",
                  backgroundColor: "#eef2ff",
                },
              }}
            >
              IPO
            </Button>

            <Button
              onClick={() => {
                setDealType("FO");
                setPage(0);
              }}
              sx={{
                px: 2.5,
                py: 0.8,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: "0.85rem",
                textTransform: "none",
                border: `2px solid ${
                  dealType === "FO" ? "#4f46e5" : "#e2e8f0"
                }`,
                color: dealType === "FO" ? "#4f46e5" : "#94a3b8",
                backgroundColor: dealType === "FO" ? "#eef2ff" : "transparent",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#4f46e5",
                  backgroundColor: "#eef2ff",
                },
              }}
            >
              FO
            </Button>
          </Box>

          <Box sx={{ width: { xs: "100%", sm: 320 }, flexShrink: 0 }}>
            <TextField
              fullWidth
              placeholder="Search by ticker, issuer name, or sector..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <SearchOutlinedIcon
                    sx={{ color: "#94a3b8", mr: 0.5, fontSize: 20 }}
                  />
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2.5,
                  background: "#f8fafc",
                  fontSize: "0.88rem",
                  "& fieldset": { borderColor: "#e2e8f0" },
                  "&:hover fieldset": { borderColor: "#cbd5e1" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#4f46e5",
                    borderWidth: "1.5px",
                  },
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {loading && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            gap: 2,
          }}
        >
          <CircularProgress size={36} thickness={4} sx={{ color: "#4f46e5" }} />
          <Typography
            sx={{ fontSize: "0.85rem", color: "#94a3b8", fontWeight: 500 }}
          >
            Loading quant analysis data...
          </Typography>
        </Box>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 2.5,
            border: "1px solid #fecaca",
            "& .MuiAlert-message": { fontSize: "0.85rem" },
          }}
        >
          {error}
        </Alert>
      )}

      {!loading && !error && filteredTickers.length === 0 && (
        <Box
          sx={{
            py: 8,
            textAlign: "center",
            background: "#ffffff",
            borderRadius: 3,
            border: "1px solid #e2e8f0",
          }}
        >
          <PublicRoundedIcon sx={{ fontSize: 48, color: "#e2e8f0", mb: 1.5 }} />
          <Typography sx={{ color: "#94a3b8", fontSize: "0.9rem" }}>
            {searchQuery
              ? "No results found."
              : "No quant analysis data available."}
          </Typography>
        </Box>
      )}

      {!loading && filteredTickers.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 3,
              border: "1px solid #e2e8f0",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              maxHeight: 600,
              overflow: "auto",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f5f9",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#cbd5e1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#94a3b8",
              },
            }}
          >
            <Table sx={{ tableLayout: "fixed", minWidth: 800 }}>
              <TableHead>
                <TableRow
                  sx={{
                    background: "#f8fafc",
                    borderBottom: "2px solid #e2e8f0",
                  }}
                >
                  <SortableHeader column="ticker" label="Ticker" width={130} />
                  <SortableHeader column="issuer_name" label="Issuer Name" width="18%" />
                  <SortableHeader column="pricing_date" label="Pricing Date" width={120} />
                  <SortableHeader column="run_date" label="Run Date" width={120} />
                  <SortableHeader column="quant_signal" label="Signal" width={90} />

                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#0f172a",
                      fontSize: "0.85rem",
                    }}
                  >
                    Summary
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredTickers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((ticker) => {
                    const parsedAnalysis = parseQuantAnalysis(
                      ticker.quant_analysis
                    );

                    // Signal priority:
                    //   1. ticker.quant_signal — set on the API row
                    //   2. parsedAnalysis.final_signal — newest payload shape
                    //   3. parsedAnalysis.signal — older shapes
                    //   4. parsedAnalysis.dashboard.hero_section.signal{.value} — V2/legacy
                    //   5. derived from parsedAnalysis.trade_setup.trade_bias — last resort
                    //      for momentum payloads that omit an explicit signal field
                    const signal =
                      ticker.quant_signal ||
                      (!Array.isArray(parsedAnalysis)
                        ? parsedAnalysis?.final_signal ||
                          parsedAnalysis?.signal ||
                          parsedAnalysis?.dashboard?.hero_section?.signal?.value ||
                          (typeof parsedAnalysis?.dashboard?.hero_section?.signal === "string"
                            ? parsedAnalysis.dashboard.hero_section.signal
                            : undefined) ||
                          deriveSignalFromBias(parsedAnalysis?.trade_setup?.trade_bias)
                        : undefined);

                    return (
                      <TableRow
                        key={ticker.unique_deal_id}
                        onClick={() => handleViewDetails(ticker)}
                        sx={{
                          background: "#ffffff",
                          "&:hover": { background: "#f8fafc" },
                          borderBottom: "1px solid #e2e8f0",
                          cursor: "pointer",
                        }}
                      >
                        <TableCell sx={{ fontSize: "0.85rem", width: 130 }}>
                          <Typography
                            sx={{ fontWeight: 600, color: "#4f46e5", fontSize: "0.85rem" }}
                          >
                            {ticker.ticker}
                          </Typography>
                          <Typography
                            component="span"
                            onClick={(e) => {
                              e.stopPropagation();
                              openRerunDialog(ticker);
                            }}
                            sx={{
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              color: "#0f172a",
                              cursor: "pointer",
                              mt: 0.4,
                              display: "inline-block",
                              textDecoration: "underline",
                              "&:hover": { opacity: 0.7 },
                            }}
                          >
                            Re-run in EMMY
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ color: "#0f172a", fontSize: "0.8rem", width: "18%", wordBreak: "break-word", whiteSpace: "normal" }}>
                          {ticker.issuer_name}
                        </TableCell>

                        <TableCell sx={{ color: "#000000", fontSize: "0.8rem" }}>
                          {ticker.pricing_date
                            ? new Date(ticker.pricing_date).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            : "Upcoming"}
                        </TableCell>

                        <TableCell sx={{ color: "#000000", fontSize: "0.8rem" }}>
                          {new Date(ticker.run_date).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            }
                          )}
                        </TableCell>

                        <TableCell sx={{ padding: "8px" }}>
                          {signal && (
                            <Chip
                              label={signal}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                backgroundColor: getSignalColor(signal).bg,
                                color: getSignalColor(signal).color,
                                border: "none",
                              }}
                            />
                          )}
                        </TableCell>

                        <TableCell sx={{ padding: "8px" }}>
                          <Typography
                            sx={{
                              color: "#312f2f",
                              lineHeight: 1.4,
                              fontSize: "0.8rem",
                              fontWeight: 500,
                              fontFamily:
                                "'Segoe UI', 'Helvetica Neue', sans-serif",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              mb: 0.75,
                            }}
                          >
                            {getSummaryFromBlocks(ticker.quant_analysis)}
                          </Typography>

                          <Link
                            component="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(ticker);
                            }}
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: "#4f46e5",
                              textDecoration: "none",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: 0,
                              "&:hover": {
                                textDecoration: "underline",
                                color: "#3730a3",
                              },
                            }}
                          >
                            Read more
                          </Link>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredTickers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            sx={{
              borderTop: "1px solid #e2e8f0",
              background: "#ffffff",
              borderRadius: "0 0 12px 12px",
              "& .MuiTablePagination-toolbar": {
                paddingRight: 2,
              },
            }}
          />
        </Box>
      )}

      {/* Re-run in EMMY confirmation dialog */}
      <Dialog
        open={!!rerunRow}
        onClose={closeRerunDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, border: "1px solid #e2e8f0" },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#0f172a", pb: 1 }}>
          Re-run in EMMY
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#475569", mb: 1.25 }}>
            EMMY will be asked (edit if needed):
          </Typography>
          <TextField
            value={rerunQuery}
            onChange={(e) => setRerunQuery(e.target.value)}
            multiline
            minRows={2}
            maxRows={6}
            fullWidth
            size="small"
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                bgcolor: "#eef2ff",
                borderRadius: 1.5,
                fontFamily: "'JetBrains Mono', 'SF Mono', monospace",
                fontSize: "0.82rem",
                lineHeight: 1.5,
                color: "#0f172a",
                "& fieldset": { borderColor: "#e2e8f0" },
                "&:hover fieldset": { borderColor: "#4f46e5" },
                "&.Mui-focused fieldset": { borderColor: "#4f46e5" },
              },
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a", mb: 0.5 }}>
            Send in:
          </Typography>
          <RadioGroup
            value={rerunMode}
            onChange={(e) => setRerunMode(e.target.value as "new" | "append")}
          >
            <FormControlLabel
              value="new"
              control={
                <Radio
                  size="small"
                  sx={{ color: "#4f46e5", "&.Mui-checked": { color: "#4f46e5" } }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#1e293b" }}>
                  New chat{" "}
                  <Typography component="span" variant="caption" sx={{ color: "#475569" }}>
                    (recommended — clean context)
                  </Typography>
                </Typography>
              }
            />
            <FormControlLabel
              value="append"
              control={
                <Radio
                  size="small"
                  sx={{ color: "#4f46e5", "&.Mui-checked": { color: "#4f46e5" } }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#1e293b" }}>
                  Append to my current EMMY chat
                </Typography>
              }
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={closeRerunDialog} sx={{ textTransform: "none", color: "#475569" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmRerun}
            disabled={!rerunQuery.trim()}
            sx={{
              textTransform: "none",
              bgcolor: "#4f46e5",
              "&:hover": { bgcolor: "#3730a3" },
            }}
          >
            Yes, ask EMMY
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShowQuantAnalysis;
